import { useState, ReactElement, useEffect } from "react";
import Modal from "../../Modal";
import Button from "../../Button";
import { Text } from "../../Type";
import styles from "./index.module.scss";

type PropsType = {
  children: ReactElement | ReactElement[];
};

const TIME_INTERVAL = 10 * 60 * 1000; // Check every 10 minutes

const getAssestManifestContentFile = async () => {
  const response = await fetch("/asset-manifest.json", {
    method: "GET",
    cache: "no-store",
  });
  return response.json();
};

const VersionChecker = ({ children }: PropsType) => {
  const [warningModalIsOpen, setWarningModalIsOpen] = useState(false);

  const checkForNewVersion = async () => {
    try {
      const data: {
        entrypoints: string[];
      } = await getAssestManifestContentFile();

      // If any of the latest css or js files are not included in the
      // document, we're running an older version of the app
      const outdated = data.entrypoints
        .filter(
          (source) =>
            !source.includes("runtime-main") && !source.includes("hot-update")
        )
        .some((source) => {
          const tag = document.querySelector(
            `script[src="/${source}"],link[href="/${source}"]`
          );
          return !tag;
        });

      if (outdated) {
        setWarningModalIsOpen(true);
        // eslint-disable-next-line no-console
        console.info("✨ Version check: New version available");
      } else {
        // eslint-disable-next-line no-console
        console.info("✅ Version check: Up to date");
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.info("🚫 Version check: Failed");
    }
  };

  useEffect(() => {
    checkForNewVersion();
    const warningPoll = setInterval(checkForNewVersion, TIME_INTERVAL);
    return () => clearInterval(warningPoll);
  }, []);

  const handleClose = () => {
    setWarningModalIsOpen(false);
  };

  const refreshPage = () => {
    setWarningModalIsOpen(false);
    window.location.reload();
  };

  return (
    <>
      {children}
      <Modal
        isOpen={warningModalIsOpen}
        title="Newer Version"
        onClose={handleClose}
      >
        <div className={styles.content}>
          <Text>A newer version of EASI is available</Text>
        </div>
        <div className={styles.modalButton}>
          <Button primary onClick={refreshPage}>
            Refresh to update
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default VersionChecker;
